<template>
<q-page class="main">
  <q-card class="page-card">
    <q-card-section class="page-card__main-content">
      <item-header :item="ruleVersion" :reference-number="ruleVersion.rule.code">
        <template v-slot:header>
          <h1 class="heading-2 q-mb-lg">{{ $sanitize(ruleVersion.summary) }}</h1>
        </template>
        <template v-slot:type>
          <q-chip v-if="ruleVersion.rule.type" :label="$ruleTypeLabel(ruleVersion.rule.type)" class="type-chip"
                  outline />
        </template>
      </item-header>
      <common-banner type="info" class="q-mb-lg"
                     v-if="isOldVersion && ($can('manage-rules') || $can('updateBindingForProject', ruleVersion.rule_id))">
        Deze richtlijn is niet in de meest recente versie gekoppeld.
        <template v-slot:actions
                  v-if="$can('updateBindingForProject', ruleVersion.rule_id)">
          <common-form ref="updateBindingForm"
                       :action="$route('project.rule.update_binding', {project: project.id, rule: ruleVersion.rule_id})" />

          <primary-button label="Bijwerken"
                          @click="updateRuleBinding" />
        </template>
      </common-banner>
      <div v-if="leadingCategory?.content_owner" class="item__info-table">
        <div>
          <strong>Contenteigenaar/eigenaren</strong>
        </div>
        <div>
          <span>{{ leadingCategory.content_owner }}</span>
        </div>
      </div>
      <div class="item__info-table q-mb-none">
        <div>
          <strong>Bewerkers</strong>
        </div>
        <div v-if="ruleVersionUsers">
          <span class="q-mr-lg">{{ ruleVersionUsers }}</span>
        </div>
        <span v-else>
            -
          </span>
      </div>
      <template v-if="ruleVersion.motivation">
        <h4 class="q-mb-md">{{ fieldLabels.motivation }}</h4>
        <p class="editor-styling" v-html="$sanitize(ruleVersion.motivation)" />
        <template v-if="ruleVersion.description">
          <h4 class="q-mb-md">{{ fieldLabels.possible_solution }}</h4>
          <p class="editor-styling" v-html="$sanitize(ruleVersion.description)" />
        </template>
      </template>
      <template v-else>
        <h4 class="q-mb-md">{{ fieldLabels.description }}</h4>
        <p class="editor-styling" v-html="$sanitize(ruleVersion.description)" />
      </template>
    </q-card-section>
    <q-card-section class="page-card__details">
      <expansion-element
          label="Documenten"
          :items-count="ruleVersion.assets.length"
          :showEmpty="canEditCustomRule">
        <document-list
            :documents="ruleVersion.assets"
            :uploadUrl="$route('rule.add_file', [ruleVersion.rule_id, ruleVersion.id])"
            :deleteUrl="$route('rule.delete_file',  [ruleVersion.rule_id, ruleVersion.id])"
            :can-edit="canEditCustomRule"
        />
      </expansion-element>
      <expansion-element
          label="Externe links"
          :items-count="ruleVersion.links?.length || 0"
      >
        <q-list bordered separator class="rounded-borders">
          <q-item v-for="link in ruleVersion.links" :key="link.id" class="items-center">
            <q-item-section>
              <common-link :label="link.name || link.href" :href="link.href" target="_blank" />
            </q-item-section>
          </q-item>
        </q-list>
      </expansion-element>
      <expansion-element
          label="Van toepassing op fase(s)"
          :items-count="ruleVersion.phases.length"
      >
        <chip-list :items="ruleVersion.phases" />
      </expansion-element>
      <expansion-element
          label="Categorie&euml;n"
          :items-count="ruleVersion.categories.length"
      >
        <chip-list :items="categories" :leading-item="leadingCategory" />
      </expansion-element>
      <expansion-element v-if="ruleVersion.themes.length"
                         label="Thema's"
                         :items-count="ruleVersion.themes.length"
      >
        <chip-list :items="ruleVersion.themes" />
      </expansion-element>
    </q-card-section>
  </q-card>
  <sticky-top-bar />
  <sticky-bottom-bar v-if="canEditCustomRule">
    <common-fab icon="edit" label="Bewerk project-specifieke richtlijn"
                @click="$goto('custom-rule.edit', {custom_richtlijn: ruleVersion.rule})" />
  </sticky-bottom-bar>
</q-page>
</template>

<script>
import ChipList from '~HIOR/components/lists/ChipList'
import DocumentList from '~HIOR/components/lists/DocumentList'
import PhaseList from '~HIOR/components/lists/PhaseList'
import { ruleMixin } from '~HIOR/mixins'
import { ruleType } from '~HIOR/constants'
import { config } from '~INSTANCE'

export default {
  mixins: [ruleMixin],
  components: {
    ChipList,
    DocumentList,
    PhaseList,
  },
  props: {
    project: {
      type: Object,
    },
    phase: {
      type: Object,
    },
    ruleVersion: {
      type: Object,
      default: () => ({}),
    },
    phases: {
      type: Array,
    },
  },
  computed: {
    leadingCategory () {
      if (!this.ruleVersion.rule.leading_category_id) {
        return null
      }

      if (!this.ruleVersion.categories) {
        return null
      }

      return this.ruleVersion.categories.find((category) => {
        return category.id === this.ruleVersion.rule.leading_category_id
      })
    },
    categories () {
      if (this.ruleVersion.leading_category_id) {
        this.ruleVersion.categories.filter((category) => {
          return category.id !== this.ruleVersion.leading_category_id
        })
      }

      return this.ruleVersion.categories
    },
    ruleVersionUsers () {
      if (!this.ruleVersion.users) {
        return
      }
      const users = this.ruleVersion.users.map((user) => {
        return this.$fullName(user)
      })

      return users.join(', ')
    },
    canEditCustomRule () {
      return this.ruleVersion.rule.type === ruleType.TYPE_CUSTOM
          && this.$can('manage-projects')
          && this.$can('manage-rules')
    },
    isOldVersion () {
      return this.ruleVersion.rule.current_published_version?.id &&
          this.ruleVersion.rule.current_published_version?.id !== this.ruleVersion.id
    },
    fieldLabels () {
      return {
        description: config.translations?.rule?.description ?? 'Omschrijving',
        motivation: config.translations?.rule?.motivation ?? 'Motivatie',
        possible_solution: config.translations?.rule?.possible_solution ?? 'Mogelijke oplossing',
      }
    },
  },
  methods: {
    async updateRuleBinding () {
      if (!await this.$confirm(
          {
            title: 'Richtlijn bijwerken',
            messages: [`"Weet u zeker dat u de richtlijnversie voor richtlijn ${this.ruleVersion.rule.code} wilt bijwerken voor dit project van versie ${this.ruleVersion.version} naar versie ${this.ruleVersion.rule.current_published_version.version}?"`],
          },
      )) {
        return
      }
      this.$refs.updateBindingForm.submit()
    },
  },
}
</script>
